import { Injectable, NgZone } from "@angular/core";
import { Router, NavigationStart } from "@angular/router";
import { ContentType } from "../shared/contentTypes";
import { RouterHelperService } from "./routerHelper.service";
import { ObjectObserverFactory } from "./objectObserverFactory";
import { MenuSettingsDataModel2, MenuSettingsDMKey, LocationDataModel, EventDataModel, TermDataModel, NewsDataModel, CouponDataModel, ItemGroupDataModel, BonusactionDataModel } from "../shared/models";
import { CircularInjectionHelper } from "./circularInjectionHelper";
import { CoreLogService } from "./log.service";
import { UserHelperService } from "./userHelper.service";
import { LikeService } from "./like.service";
import { IObjectObserver } from "./IObjectObserver";
import { filter } from "rxjs/operators";
import Utils from "../shared/utils";
import { Subscription } from "rxjs";
import { BottomSheetHelperService } from "./bottomSheetHelper.service";
import { SelectLocationDialogComponent } from "../modalDialogs/selectLocationDialog/selectLocationDialog.component";
import { TermService } from "../components/term/term.service";

@Injectable({
  providedIn: "root"
})
export class NavigationHelperService {
  private _menuSettingObsServ: IObjectObserver<MenuSettingsDataModel2>;

  private _locationObsServ: IObjectObserver<LocationDataModel>;
  private _eventObsServ: IObjectObserver<EventDataModel>;
  private _termObsServ: IObjectObserver<TermDataModel>;
  private _newsObsServ: IObjectObserver<NewsDataModel>;
  private _couponObsServ: IObjectObserver<CouponDataModel>;
  private _itemGroupObsServ: IObjectObserver<ItemGroupDataModel>;
  private _bonusactionObsServ: IObjectObserver<BonusactionDataModel>;

  private _redirectToContentSubscription: Subscription;

  constructor(
    private _ngzone: NgZone
    , private _coreLogService: CoreLogService
    , private _router: Router
    , private _routerHelperService: RouterHelperService
    , _objObsInjectHelper: CircularInjectionHelper
    , private _userHelperService: UserHelperService
    , private _likeService: LikeService
    , private _termService: TermService
    , private _bttomSheetHelperService: BottomSheetHelperService) {
    this._menuSettingObsServ = ObjectObserverFactory.GetObjectObserverByInjectionHelper<MenuSettingsDataModel2>(ContentType.MenuSettings, _objObsInjectHelper);
    this._newsObsServ = ObjectObserverFactory.GetObjectObserverByInjectionHelper<NewsDataModel>(ContentType.News, _objObsInjectHelper);
    this._couponObsServ = ObjectObserverFactory.GetObjectObserverByInjectionHelper<CouponDataModel>(ContentType.Coupon, _objObsInjectHelper);
    this._eventObsServ = ObjectObserverFactory.GetObjectObserverByInjectionHelper<EventDataModel>(ContentType.Event, _objObsInjectHelper);
    this._itemGroupObsServ = ObjectObserverFactory.GetObjectObserverByInjectionHelper<ItemGroupDataModel>(ContentType.Item_Group, _objObsInjectHelper);
    this._bonusactionObsServ = ObjectObserverFactory.GetObjectObserverByInjectionHelper<BonusactionDataModel>(ContentType.Bonusaction, _objObsInjectHelper);
    this._termObsServ = ObjectObserverFactory.GetObjectObserverByInjectionHelper<TermDataModel>(ContentType.Term, _objObsInjectHelper);
    this._locationObsServ = ObjectObserverFactory.GetObjectObserverByInjectionHelper<LocationDataModel>(ContentType.Location, _objObsInjectHelper);

    // Unsubscribe/Cancel Redirect on other Navigation (i.e. user input)
    this._router.events.pipe(
      filter(event => event instanceof NavigationStart)
    ).subscribe(() => {
      this.safeUnsubscribeRedirect();
    });
  }

  /** Attempts to navigates to a specific Content by first checking if a content with the specified type and id exists
   * and otherwise waits for the content to arrive. Cancles waiting on any router navigation start.
   * @param contentType contentType
   * @param contentId Id of content
   * @param time time to listen for contenttype changes, in case no matching content can be found; -1 = inifinite till user navigation
   */
  public async tryNavigateToContent(contentType: number, contentId: number, time: number = -1): Promise<void> {
    const me = this;
    if (contentId) {
      if (await me.doesContentExist(contentType, contentId)) {
        me.navigateToContent(contentType, contentId);
      } else {
        me.safeUnsubscribeRedirect();
        me._redirectToContentSubscription = this.subscribeToContentType(contentType, async () => {
          if (await me.doesContentExist(contentType, contentId)) {
            me.navigateToContent(contentType, contentId);
            me.safeUnsubscribeRedirect();
          }
        });
        if (time >= 0) {
          setTimeout(me.safeUnsubscribeRedirect, time);
        }
      }
    } else {
      me.navigateToContent(contentType, null);
    }
  }

  /** Checks if a content with the specified type and id exists.
   * @param contentType contentType
   * @param contentId Id of content
   */
  private async doesContentExist(contentType: number, contentId: number): Promise<boolean> {
    const me = this;
    switch (contentType) {
      case ContentType.Location: { return await me._locationObsServ.ContainsByFilter((obj) => obj.hyp_cus_nr === contentId); break; }
      case ContentType.News: { return await me._newsObsServ.ContainsByFilter((obj) => obj.id === contentId); break; }
      case ContentType.Event: { return await me._eventObsServ.ContainsByFilter((obj) => obj.id === contentId); break; }
      case ContentType.Coupon: { return await me._couponObsServ.ContainsByFilter((obj) => obj.id === contentId); break; }
      case ContentType.Item_Group: { return await me._itemGroupObsServ.ContainsByFilter((obj) => obj.id === contentId); break; }
      case ContentType.Bonussystem: { return await me._bonusactionObsServ.ContainsByFilter((obj) => obj.id === contentId); break; }
      case ContentType.Term: { return await me._termObsServ.ContainsByFilter((obj) => obj.id === contentId); break; }
      default: { me._coreLogService.error("NavigationHelper.doesContentExist", "Unknown ContentType", contentType); }
    }
    return false;
  }
  /** Returns a Subscription that listens for changes of a contenttype
   * @param contentType contentType
   * @param callback callback execute on changes for the contenttype specified
   */
  private subscribeToContentType(contentType: number, callback: () => void): Subscription {
    const me = this;
    let subscription: Subscription;
    switch (contentType) {
      case ContentType.News: { subscription = me._newsObsServ.ObserveChanges.subscribe(callback); break; }
      case ContentType.Event: { subscription = me._eventObsServ.ObserveChanges.subscribe(callback); break; }
      case ContentType.Coupon: { subscription = me._couponObsServ.ObserveChanges.subscribe(callback); break; }
      case ContentType.Location: { subscription = me._locationObsServ.ObserveChanges.subscribe(callback); break; }
      case ContentType.Item_Group: { subscription = me._itemGroupObsServ.ObserveChanges.subscribe(callback); break; }
      case ContentType.Bonussystem: { subscription = me._bonusactionObsServ.ObserveChanges.subscribe(callback); break; }
      case ContentType.Term: { subscription = me._termObsServ.ObserveChanges.subscribe(callback); break; }
      default: { me._coreLogService.error("NavigationHelper.subscribeToContentTypeFor", "Unknown ContentType", contentType); }
    }
    return subscription;
  }

  private safeUnsubscribeRedirect() {
    if (!Utils.IsNull(this._redirectToContentSubscription)) {
      this._redirectToContentSubscription.unsubscribe();
    }
  }

  /** Navigates to a specific Content
   * @param contentType contentType
   * @param contentId Id of content
   */
  public navigateToContent(contentType: number, contentId: number, contentExtra: string = null): void {
    const me = this;
    this._ngzone.run(() => {
      if (contentType > -1) {
        let path = me.getPathForContentType(contentType);
        if (contentId && contentId > 0) {
          switch (contentType) {
            case ContentType.Location:
            case ContentType.Event:
              {
                path = path + "detail";
                me._router.navigate([path, contentId]);
                break;
              }
            case ContentType.Term:
              {
                me.openTermModel(contentId);
                break;
              }
            case ContentType.News:
            case ContentType.Coupon:
              {
                me._routerHelperService.setRouteTargetDataByKey("targetScrollId", contentId);
                me._router.navigateByUrl(path);
                break;
              }
            case ContentType.Item_Group:
              {
                // TODO: Adjust after Figuring out userflow
                me._router.navigateByUrl(path);
                // me.navigateToItemGroup(null, contentId);
                break;
              }
            case ContentType.Bonussystem:
              {
                path = path + "detail";
                me._router.navigate([path, contentId]);
                break;
              }
            case ContentType.ExternalLink:
              {
                if (contentExtra) {
                  window.open(contentExtra, "_system");
                }
                break;
              }
            default:
              {
                me._router.navigateByUrl(path);
                break;
              }
          }
        } else {
          me._router.navigateByUrl(path);
        }
      } else {
        // DO Nothing
      }
    });
  }

  /**
    * open a modal dialog to show term detail
    * @param term information for term and condition
    */
  public openTermModel(termId: number): void {
    const me = this;
    me._termService.openTermModel(termId);
  }

  public getPathForContentType(content_type: ContentType): string {
    switch (content_type) {
      case ContentType.Home_Slider:
        return "/";
      case ContentType.Location:
        return "/location";
      case ContentType.News:
        return "/news";
      case ContentType.Event:
        return "/event";
      case ContentType.Coupon:
        return "/coupon";
      case ContentType.Reservation:
        return "/reservation";
      case ContentType.WebVoucher:
        return "/webvoucherdetail";
      case ContentType.Bonussystem:
        return "/bonusaction";
      case ContentType.Item_Group:
        return "/menugroup";
      case ContentType.Menu:
        return "/menugroup";
      case ContentType.Impressum:
        return "/termcondition";
      default:
        // todo log unknown content type
        return "/";
    }
  }

  /** Navigates to a specific ItemGroup of a Location. When no Location is provided, it
   * either looksup if the user is logged in and has only a single liked Location, or opens
   * a dialog for the user to choose.
   * @params locationId number representing the ID of the Location. Can be null
   * @params itemGroupId number ContentID of the ItemGroup
  */
  public async navigateToItemGroup(locationId: number, itemGroupId: number) {
    const me = this;
    const menuSettingLocationIds = await this.getMenuSettingLocationIds();
    // Check if the Location is forced by the InitialQueryParam loc
    const locQueryParam = me._routerHelperService.getInitialQueryParameters()["loc"];
    const forcedEMenuLocationID = +(locQueryParam);
    this._locationObsServ.GetObjectsByFilter(location => location.eMenuLocationID === forcedEMenuLocationID)
      .then(forcedLocationArray => {
        const forcedLocation = forcedLocationArray[0];
        if (forcedLocation && !forcedLocation.deleted && forcedLocation.participate) {
          me._router.navigate(["/menu", itemGroupId, forcedLocation.hyp_cus_nr]);
        } else if (locationId) {
          if (menuSettingLocationIds.includes(locationId)) {
            me.navigateToItemGroupInLocation(locationId, itemGroupId);
          } else {
            me.openFilterDialog(itemGroupId, menuSettingLocationIds);
          }
        } else {
          me.getSingleLikedLocation().then((singleLikedLocationId) => {
            if (menuSettingLocationIds.includes(singleLikedLocationId)) {
              me.navigateToItemGroupInLocation(singleLikedLocationId, itemGroupId);
            } else {
              me.openFilterDialog(itemGroupId, menuSettingLocationIds);
            }
          }).catch(e => {
            me._coreLogService.error("NavigationHelper.navigateToItemGroup", "getSingleLikedLocation", e);
          });
        }
      });
  }

  /**
 * Open a ModalDialog for choosing the Location before navigating to a specific itemGroup
 */
  private openFilterDialog(itemGroupId: number, menuSettingLocationIds: number[]) {
    const me = this;
    const bottomSheetLocationRef = me._bttomSheetHelperService.open(SelectLocationDialogComponent, {
      data: menuSettingLocationIds
    });
    // navigate to specific itemGroupId
    bottomSheetLocationRef.afterDismissed().subscribe((result: number) => {
      if (result) {
        me.navigateToItemGroupInLocation(result, itemGroupId);
      }
    });
  }

  private navigateToItemGroupInLocation(locationId: number, ItemGroupId: number) {
    const me = this;
    me._router.navigate(["/menu", ItemGroupId]);
  }

  private getSingleLikedLocation(): Promise<number> {
    const me = this;
    return new Promise<number>((resolve) => {
      if (me._userHelperService.IsRegistered) {
        me._likeService.GetContentIDForActiveLikes(ContentType.Location)
          .then(locations => {
            if (locations && locations.length === 1) {
              resolve(locations[0]);
            } else {
              resolve(null);
            }
          })
          .catch(e => {
            me._coreLogService.error("NavigationHelper.getSingleLikedLocation", "GetContentIDForActiveLikes", e);
            resolve(null);
          });
      } else {
        resolve(null);
      }
    });
  }

  private getMenuSettingLocationIds(): Promise<number[]> {
    const me = this;
    return new Promise<number[]>((resolve, reject) => {
      me._menuSettingObsServ.GetObject(MenuSettingsDMKey)
        .then(menusetting => {
          resolve(menusetting.locations);
        })
        .catch(e => {
          me._coreLogService.error("NavigationHelper.getMenuSettingLocationIds", "_menuSettingObsServ.GetObject", e);
          reject();
        });
    });
  }
}
